import React from "react";
import {
  Badge,
  Box,
  Circle,
  Flex,
  HStack,
  Text,
  VStack,
  useBreakpointValue,
  useColorModeValue,
} from "@chakra-ui/react";
import { BsCheck, BsLightningChargeFill, BsX } from "react-icons/bs";
import { formatDateTime } from "../../utils/riskReviewFormatters";

const statusMeta = {
  completed: { label: "Approved", colorScheme: "green", color: "green.500" },
  current: { label: "In Progress", colorScheme: "blue", color: "blue.500" },
  bench: { label: "Bench", colorScheme: "yellow", color: "yellow.500" },
  rejected: { label: "Rejected", colorScheme: "red", color: "red.500" },
  pending: { label: "Pending", colorScheme: "gray", color: "gray.300" },
};

const getSteps = (approvals) => {
  const grouped = approvals.reduce((map, approval) => {
    const key = approval.level || 1;
    map[key] = map[key] || [];
    map[key].push(approval);
    return map;
  }, {});

  return Object.entries(grouped).map(([level, items]) => {
    const bench = items.some((item) => item.action === "BENCH_APPROVE");
    const rejected = items.some((item) => item.status === "REJECTED");
    const completed = items.length > 0 && items.every((item) => item.status === "APPROVED");
    const current = items.some((item) => item.status === "PENDING");
    const first = items[0];
    return {
      id: level,
      name: first.stageName || `Level ${level}`,
      status: bench ? "bench" : rejected ? "rejected" : completed ? "completed" : current ? "current" : "pending",
      details: items,
    };
  });
};

const RiskReviewApprovalTimeline = ({ approvals = [] }) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const isMobile = useBreakpointValue({ base: true, md: false });
  const steps = getSteps(approvals);

  return (
    <Box bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" overflow="hidden">
      <Box px={4} py={3} borderBottom="1px solid" borderColor="gray.100">
        <Text color="gray.800" fontWeight="700">
          Approval Progress
        </Text>
      </Box>
      <Box px={4} py={4}>
        {steps.length ? (
          <VStack align="stretch" spacing={4}>
            <Flex direction={isMobile ? "column" : "row"} align={isMobile ? "stretch" : "flex-start"} gap={0}>
              {steps.map((step, index) => {
                const meta = statusMeta[step.status];
                return (
                  <Flex key={step.id} flex={1} align={isMobile ? "flex-start" : "center"}>
                    <VStack spacing={2} minW={isMobile ? "44px" : "110px"} align="center">
                      <Circle size="36px" bg={meta.color} color="white" border={step.status === "pending" ? "2px solid" : "0"} borderColor="gray.300">
                        {step.status === "completed" ? <Box as={BsCheck} boxSize={5} /> : null}
                        {step.status === "bench" ? <Box as={BsLightningChargeFill} boxSize={4} /> : null}
                        {step.status === "rejected" ? <Box as={BsX} boxSize={5} /> : null}
                        {!["completed", "bench", "rejected"].includes(step.status) ? <Box boxSize={2} borderRadius="full" bg="currentColor" /> : null}
                      </Circle>
                      <Text fontSize="xs" fontWeight="600" textAlign="center">{step.name}</Text>
                      <Badge colorScheme={meta.colorScheme} fontSize="2xs">{meta.label}</Badge>
                    </VStack>
                    {index < steps.length - 1 ? (
                      <Box flex={1} h={isMobile ? "44px" : "4px"} w={isMobile ? "4px" : "auto"} minW={isMobile ? "4px" : "40px"} bg={step.status === "completed" || step.status === "bench" ? "green.400" : "gray.200"} mt={isMobile ? 0 : 0} />
                    ) : null}
                  </Flex>
                );
              })}
            </Flex>
            <VStack align="stretch" spacing={2}>
              {approvals.map((approval) => (
                <HStack key={approval._id} justify="space-between" align="flex-start" fontSize="xs" color="gray.500">
                  <Text>{approval.stageName || `Level ${approval.level}`} · {approval.approverRole || approval.approverType}</Text>
                  <Text>{approval.action === "BENCH_APPROVE" ? "BENCH APPROVED" : approval.status}{approval.actedAt ? ` · ${formatDateTime(approval.actedAt)}` : ""}</Text>
                </HStack>
              ))}
            </VStack>
          </VStack>
        ) : (
          <Text color="gray.500" fontSize="sm">
            No approval flow has been generated yet.
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default RiskReviewApprovalTimeline;
